// CELL-BIO9 — boot. Show slot select screen, then resume or start Chapter 1.
// Wire stat bar, settings panel (CRT, audio, text speed, save export/import),
// and optional warp overlay.

import { gameState } from "./engine/gameState";
import { loadSlot, setActiveSlot } from "./engine/saveManager";
import { StoryManager } from "./engine/storyManager";
import { Terminal } from "./engine/terminal";
import { audioEngine } from "./engine/audio";
import { initStatBar } from "./ui/statBar";
import { showSlotSelect } from "./ui/slotSelect";
import { initSettings, loadSpeedPref } from "./ui/settings";
import { warpOverlay } from "./fx/warp";

const START_NODE = "ch1_wake";

function mustGet(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id} in index.html`);
  return el;
}

function initCrtToggle(): void {
  // CRT state is now managed by the settings panel.
  // This function only restores the persisted preference on boot.
  const stored = localStorage.getItem("cellbio9_crt");
  if (stored === "off") document.body.classList.remove("crt");

  // Sync warp overlay to initial CRT state.
  if (document.body.classList.contains("crt")) {
    warpOverlay.attach();
  } else {
    warpOverlay.detach();
  }
}

function initAudioPref(): void {
  // Restore mute preference on boot.
  const storedMute = localStorage.getItem("cellbio9_mute");
  if (storedMute === "on") audioEngine.setMuted(true);
}

// Unlock AudioContext on first user gesture.
function hookFirstGesture(): void {
  const unlock = (): void => {
    audioEngine.start();
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("pointerdown", unlock);
  };
  window.addEventListener("keydown", unlock);
  window.addEventListener("pointerdown", unlock);
}

async function boot(): Promise<void> {
  const terminalEl = mustGet("terminal");
  const choicesEl = mustGet("choices");

  const terminal = new Terminal(terminalEl);
  const story = new StoryManager(terminal, choicesEl);

  // Apply stored text speed before any printing.
  terminal.setSpeed(loadSpeedPref());

  initStatBar(mustGet("statbar"), gameState);
  initCrtToggle();
  initAudioPref();
  hookFirstGesture();

  // Settings panel wires [SETTINGS] button into #sysbar.
  // Must be called after initAudioPref so mute state is correct.
  // Pass warp sync callback so settings.ts never needs a dynamic import.
  initSettings(terminal, (crtOn) => {
    if (crtOn) warpOverlay.attach(); else warpOverlay.detach();
  });

  // ── Slot select screen ──────────────────────────────────────
  const { slot, fresh } = await showSlotSelect(terminalEl, choicesEl);
  setActiveSlot(slot);

  // Clear terminal output from slot select before game starts.
  terminal.clear();

  terminal.printInstant("BLACKREACH PENITENTIARY // TERMINAL LINK ESTABLISHED", "system");
  terminal.printInstant("CELL-BIO9 // CHAPTER 1: THE CELL", "system");

  if (!fresh) {
    const save = loadSlot(slot);
    if (save && story.has(save.currentNode)) {
      gameState.restore(save);
      terminal.printInstant(`[AUTOSAVE FOUND — RESUMING DAY ${save.day}]`, "system");
      await story.goto(save.currentNode);
      return;
    }
  }

  // Fresh start (or save was invalid).
  await story.goto(START_NODE);
}

void boot();
